import convert from 'convert';

import { isFiniteNumber } from '../numbers';
import { Nil } from '../type-utils';

import {
    AccelerationDisplayUnits,
    AngleDisplayUnits,
    DisplaySymbol,
    DisplaySymbolMap,
    DisplayUnit,
    DisplayUnitMap,
    LengthDisplayUnits,
    SpeedDisplayUnits,
    UserPreferredUnit,
    UserPreferredUnitMap,
} from './convertUnit';
import { convertDisplayUnitValueToMeter } from './motionConvertor';

export type Unit = 'm' | 'm/s' | 'm/s^2' | 'rad' | 'rad/s' | 'rad/s^2' | 'rad^2/s' | 'ratio' | 's' | '-';

const validUnits: Unit[] = ['m', 'm/s', 'm/s^2', 'rad', 'rad/s', 'rad/s^2', 'rad^2/s', 'ratio', 's'];

export const isUnit = (value: unknown): value is Unit =>
    typeof value === 'string' && validUnits.includes(value as Unit);

const isLengthDisplayUnit = (unit: string): unit is LengthDisplayUnits => {
    return unit === 'in' || unit === 'cm';
};

export const isAngleDisplayUnit = (unit: string): unit is AngleDisplayUnits => {
    return unit === 'deg' || unit === 'deg/s' || unit === 'deg/s^2' || unit === 'deg^2/s';
};

const isAccelerationDisplayUnit = (unit: string): unit is AccelerationDisplayUnits => {
    return unit === 'cm/s^2' || unit === 'in/s^2';
};

export const isSpeedDisplayUnit = (unit: string): unit is SpeedDisplayUnits => {
    return unit === 'km/h' || unit === 'cm/s' || unit === 'mph' || unit === 'in/s';
};

export function convertDisplayValueToNormalizedDbValues(args: { value: null; displayUnit: DisplayUnit }): null;
export function convertDisplayValueToNormalizedDbValues(args: {
    value: undefined;
    displayUnit: DisplayUnit;
}): undefined;
export function convertDisplayValueToNormalizedDbValues(args: { value: number; displayUnit: DisplayUnit }): number;

export function convertDisplayValueToNormalizedDbValues({
    value,
    displayUnit,
}: {
    value: number | Nil;
    displayUnit: DisplayUnit;
}): number | Nil {
    if (!isFiniteNumber(value)) {
        return value;
    }

    // The rest of the function logic remains the same, but it's guaranteed to return a number.
    if (displayUnit === 'percent') {
        return value / 100;
    }

    if (isSpeedDisplayUnit(displayUnit) || isAccelerationDisplayUnit(displayUnit)) {
        return convertDisplayUnitValueToMeter(value, displayUnit);
    }

    if (isLengthDisplayUnit(displayUnit)) {
        return convert(value, displayUnit).to('m');
    }

    if (isAngleDisplayUnit(displayUnit)) {
        if (displayUnit === 'deg^2/s') {
            return value * convert(1, 'deg').to('rad') ** 2;
        }

        return convert(value, 'deg').to('rad');
    }

    throw new Error(`Unhandled unit: ${displayUnit}`);
}

export function getDisplayUnitAndSymbol(
    unit: Unit,
    userPreferredUnit: UserPreferredUnit,
    useHours = true,
): { unit: DisplayUnit; symbol: DisplaySymbol } {
    const metric = userPreferredUnit === UserPreferredUnitMap.metric;
    const imperial = userPreferredUnit === UserPreferredUnitMap.imperial;
    switch (unit) {
        case 'm':
            return {
                unit: imperial ? DisplayUnitMap.in : DisplayUnitMap.cm,
                symbol: imperial ? DisplaySymbolMap.in : DisplaySymbolMap.cm,
            };

        case 'm/s':
            if (useHours) {
                return {
                    unit: !metric ? DisplayUnitMap.mph : DisplayUnitMap['km/h'],
                    symbol: !metric ? DisplaySymbolMap.mph : DisplaySymbolMap['km/h'],
                };
            }
            return {
                unit: imperial ? DisplayUnitMap['in/s'] : DisplayUnitMap['cm/s'],
                symbol: imperial ? DisplaySymbolMap['in/s'] : DisplaySymbolMap['cm/s'],
            };

        case 'm/s^2':
            return {
                unit: imperial ? DisplayUnitMap['in/s^2'] : DisplayUnitMap['cm/s^2'],
                symbol: imperial ? DisplaySymbolMap['in/s^2'] : DisplaySymbolMap['cm/s^2'],
            };

        case 'rad':
            return {
                unit: DisplayUnitMap.deg,
                symbol: DisplaySymbolMap.deg,
            };

        case 'rad/s':
            return {
                unit: DisplayUnitMap['deg/s'],
                symbol: DisplaySymbolMap['deg/s'],
            };

        case 'rad/s^2':
            return {
                unit: DisplayUnitMap['deg/s^2'],
                symbol: DisplaySymbolMap['deg/s^2'],
            };
        case 'rad^2/s':
            return {
                unit: DisplayUnitMap['deg^2/s'],
                symbol: DisplaySymbolMap['deg^2/s'],
            };
        case 'ratio':
            return {
                unit: DisplayUnitMap.percent,
                symbol: DisplaySymbolMap.percent,
            };
        case 's':
            return {
                unit: DisplayUnitMap.s,
                symbol: DisplaySymbolMap.s,
            };
        default: {
            // If we did not explicitly ask for passthrough we log the case in the console.
            if (unit !== '-') console.warn('Unhandled unit in getDisplayUnitAndSymbol', unit);
            return {
                unit: '-',
                symbol: '-',
            };
        }
    }
}
